import { AlertCircle, Loader2, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProbeCard } from '@/components/dashboard/probe-card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface MonitorStats {
  id: number;
  name: string;
  enabled: boolean;
  next_check: string | null;
  total_checks: number;
  successful_checks: number;
  last_response_time: number | null;
  last_checked_at: string | null;
}

// The checker polls every second and permits a check to run for up to one minute.
// Two minutes avoids flagging ordinary queueing delay while surfacing missed schedules.
const CHECK_OVERDUE_GRACE_MS = 2 * 60 * 1000;

type DashboardErrorResponse = {
  error?:
    | string
    | {
        code?: string;
        message?: string;
      };
};

async function getDashboardErrorMessage(response: Response): Promise<string> {
  const fallback = 'Unable to load dashboard statistics. Please try again.';

  try {
    const body = (await response.json()) as DashboardErrorResponse;
    if (typeof body.error === 'string') {
      return body.error;
    }
    if (body.error?.code === 'DEPENDENCY_UNAVAILABLE') {
      return 'Dashboard data is temporarily unavailable. Please try again.';
    }
    if (body.error?.message) {
      return body.error.message;
    }
  } catch {
    return fallback;
  }

  return fallback;
}

function isCheckOverdue(enabled: boolean, nextCheck: string | null): boolean {
  if (!enabled || !nextCheck) {
    return false;
  }

  const nextCheckTime = new Date(nextCheck).getTime();
  return Number.isFinite(nextCheckTime) && nextCheckTime < Date.now() - CHECK_OVERDUE_GRACE_MS;
}

export function DashboardPage() {
  const [monitors, setMonitors] = useState<MonitorStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/monitors/dashboard-stats', {
        credentials: 'include',
      });
      if (response.ok) {
        const data: MonitorStats[] = await response.json();
        setMonitors(data);
      } else {
        setError(await getDashboardErrorMessage(response));
      }
    } catch {
      setError('Unable to load dashboard statistics. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Monitor statistics for the last 60 minutes</p>
      </div>

      {error ? (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to load dashboard</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>{error}</span>
            <Button type="button" variant="outline" size="sm" onClick={fetchDashboardStats}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry dashboard
            </Button>
          </AlertDescription>
        </Alert>
      ) : monitors.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <p className="text-gray-500 mb-4">No monitoring probes configured yet.</p>
          <p className="text-sm text-gray-400">
            Start monitoring your websites by creating your first probe.
          </p>
          <Link
            to="/dashboard/monitors/new"
            className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-slate-50 transition-colors hover:bg-slate-900/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create a probe
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {monitors.map((monitor) => (
            <ProbeCard
              key={monitor.id}
              id={monitor.id}
              name={monitor.name}
              enabled={monitor.enabled}
              totalChecks={monitor.total_checks}
              successfulChecks={monitor.successful_checks}
              averageResponseTime={monitor.last_response_time}
              lastCheckedAt={monitor.last_checked_at}
              isOverdue={isCheckOverdue(monitor.enabled, monitor.next_check)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
